/**
 * AssetLibraryManager — the full asset-management UI for one
 * contributor-owned arche.
 *
 * Sections:
 *   1. Header: enable/disable toggle, suspended banner,
 *      quota meter, publish-library action, billing summary.
 *   2. Uploader: drag-drop / file picker, multi-file with progress.
 *   3. Asset grid: paginated cards with thumbnail (from contributor
 *      preview endpoint), tags, status, inline tag editing / delete.
 *   4. "Shared from workspaces": read-only grid of the assets a publish
 *      would pin from linked Workspace libraries (ADR-019). Those assets
 *      are curated on the Workspace Assets page, never here.
 *
 * The uploader, grid and card are the shared `asset-library/` components,
 * driven through the per-arche adapter — the Workspace Assets page renders
 * the same components through the workspace adapter.
 *
 * Read-only for unauthorized states: when the library is disabled
 * or suspended, the list still loads but uploads are blocked.
 */

import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { api, ApiError, type ContributorArcheAsset } from '@/lib/api';
import {
  useArcheAssetLibraryConfig,
  useArcheAssetBillingSummary,
  useEnableArcheAssetLibrary,
  useDisableArcheAssetLibrary,
  useSnapshotArcheAssetManifest,
  useArcheSharedAssets,
  useArcheWorkspaceLinks,
} from '../hooks/useArcheAssetLibrary';
import {
  AssetCard,
  AssetGrid,
  UploadDropzone,
  formatBytes,
  useArcheAssetLibraryAdapter,
} from './asset-library';

interface AssetLibraryManagerProps {
  archeId: string;
}

/**
 * "Pinned 2 workspace libraries; dropped 1; 1 not yet published" — the
 * snapshot response carries holder ids only (no display names), so this is
 * counts only. Empty string when no workspace library was involved.
 *
 * Mirrored by `describeWorkspacePins` in
 * `packages/plugin-dev-harness/src/cli/assets/publish.ts`, which reports the
 * same event for the same publish from the CLI. That package is on npm and
 * cannot import this one, so the copy is unavoidable — change both together.
 */
function describeWorkspacePinOutcome(result: {
  pinnedHolderIds?: string[];
  droppedHolderIds?: string[];
  unpublishedHolderIds?: string[];
}): string {
  const pinned = result.pinnedHolderIds?.length ?? 0;
  const dropped = result.droppedHolderIds?.length ?? 0;
  const unpublished = result.unpublishedHolderIds?.length ?? 0;
  if (pinned === 0 && dropped === 0 && unpublished === 0) return '';
  const parts = [`Pinned ${pinned} workspace ${pinned === 1 ? 'library' : 'libraries'}`];
  if (dropped > 0) parts.push(`dropped ${dropped}`);
  if (unpublished > 0) parts.push(`${unpublished} not yet published`);
  return `${parts.join('; ')}.`;
}

function toggleErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code === 'FEATURE_NOT_AVAILABLE') {
    return 'Self-service asset libraries are not yet available on this platform. Contact Fias support to enable one for this arche.';
  }
  return err instanceof Error ? err.message : 'Something went wrong.';
}

export function AssetLibraryManager({ archeId }: AssetLibraryManagerProps) {
  const adapter = useArcheAssetLibraryAdapter(archeId);
  const configQuery = useArcheAssetLibraryConfig(archeId);
  const billingQuery = useArcheAssetBillingSummary(archeId);
  const enableMutation = useEnableArcheAssetLibrary(archeId);
  const disableMutation = useDisableArcheAssetLibrary(archeId);
  const snapshotMutation = useSnapshotArcheAssetManifest(archeId);

  if (configQuery.isLoading) {
    return <div className="text-muted-foreground">Loading…</div>;
  }
  if (configQuery.error || !configQuery.data) {
    // A 403 here is not a failure to explain away — it is the expected answer for
    // an arche the caller does not own. `PLATFORM_OWNED_ARCHE` is the specific
    // case that matters: after an annex the arche's contributor is the Fias
    // platform account, so its OWN library becomes platform-managed (its storage
    // bills the contributor of record, and there is no per-arche funding switch).
    // The shared workspace libraries section below is unaffected — those routes
    // gate on `canPublishArche`, not contributor equality — so this must read as
    // information, not as a broken page.
    const apiError = configQuery.error instanceof ApiError ? configQuery.error : null;
    if (apiError?.status === 403) {
      const platformOwned = apiError.code === 'PLATFORM_OWNED_ARCHE';
      return (
        <Card>
          <CardHeader>
            <CardTitle>Asset library</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {platformOwned
                ? 'This plugin is owned by Fias. Its own asset library is managed by the platform and is not editable from the contributor portal. Shared workspace libraries below still apply.'
                : 'You do not own this arche, so its own asset library is not editable here.'}
            </p>
          </CardContent>
        </Card>
      );
    }
    return <div className="text-destructive">Failed to load asset library config.</div>;
  }

  const config = configQuery.data.config;
  const isSuspended = !!config.suspendedAt;
  const isEnabled = config.enabled;
  const usagePct =
    config.effectiveCaps.maxStorageBytes > 0
      ? (config.usage.activeStorageBytes / config.effectiveCaps.maxStorageBytes) * 100
      : 0;
  const pinOutcome = snapshotMutation.isSuccess
    ? describeWorkspacePinOutcome(snapshotMutation.data)
    : '';

  return (
    <div className="space-y-6">
      {isSuspended && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-6">
            <div className="font-semibold text-destructive">Asset library suspended</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Your asset library is suspended due to insufficient credits ({config.suspendReason}).
              Top up your balance to resume uploads — already-published manifests continue to serve
              to your end users.
            </div>
            <Button variant="outline" className="mt-3" asChild>
              <a href="/credits">Top up credits</a>
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Asset Library</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{isEnabled ? 'Enabled' : 'Disabled'}</div>
              <div className="text-sm text-muted-foreground">
                {isEnabled
                  ? 'Contributors can upload and edit. Runtime byte-serve continues.'
                  : 'Uploads are blocked. Already-published manifests continue to serve.'}
              </div>
            </div>
            <Switch
              checked={isEnabled}
              onCheckedChange={(next) => {
                if (next) enableMutation.mutate();
                else disableMutation.mutate();
              }}
              disabled={enableMutation.isPending || disableMutation.isPending || isSuspended}
              aria-label="Toggle asset library enabled state"
            />
          </div>
          {(enableMutation.isError || disableMutation.isError) && (
            <div role="alert" className="mt-2 text-sm text-destructive">
              {toggleErrorMessage(enableMutation.error ?? disableMutation.error)}
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium">Storage</div>
              <div className="text-sm text-muted-foreground">
                {formatBytes(config.usage.activeStorageBytes)} /{' '}
                {formatBytes(config.effectiveCaps.maxStorageBytes)} ({usagePct.toFixed(1)}%)
              </div>
              <div className="mt-1 h-2 w-full overflow-hidden rounded bg-muted">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.min(100, usagePct)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="text-sm font-medium">Assets</div>
              <div className="text-sm text-muted-foreground">
                {config.usage.activeAssetCount.toLocaleString()} /{' '}
                {config.effectiveCaps.maxAssets.toLocaleString()}
              </div>
            </div>
          </div>

          {/*
            The plugin runtime reads the published MANIFEST, not these asset
            rows, so on a LIVE arche an upload is invisible until something
            snapshots it. Publishing the arche does that automatically; this
            is the standalone path for "I only added art", and the only
            browser equivalent of `fias-dev assets publish --refresh`.
          */}
          {isEnabled && (
            <div className="mt-6 flex items-start justify-between gap-4 border-t pt-4">
              <div>
                <div className="text-sm font-medium">Publish library</div>
                <div className="text-sm text-muted-foreground">
                  Makes your current assets visible to the live version of this arche. Publishing
                  the arche itself already does this — use it when you have only added or removed
                  art. It does not affect the arche builder preview, which always shows your current
                  assets.
                </div>
                {snapshotMutation.isSuccess && (
                  <div className="mt-1 text-sm text-muted-foreground">
                    Published {snapshotMutation.data.manifest.assetCount.toLocaleString()} asset
                    {snapshotMutation.data.manifest.assetCount === 1 ? '' : 's'}.
                    {pinOutcome ? ` ${pinOutcome}` : ''}
                  </div>
                )}
                {snapshotMutation.isError && (
                  <div className="mt-1 text-sm text-destructive">
                    Could not publish the library. Try again.
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => snapshotMutation.mutate()}
                disabled={snapshotMutation.isPending || isSuspended}
              >
                {snapshotMutation.isPending ? 'Publishing…' : 'Publish library'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <BillingSummarySection
        loading={billingQuery.isLoading}
        summary={billingQuery.data?.summary}
      />

      {isEnabled && !isSuspended && <UploadDropzone adapter={adapter} />}

      <AssetGrid adapter={adapter} readOnly={!isEnabled || isSuspended} />

      <SharedWorkspaceAssetsGrid archeId={archeId} />
    </div>
  );
}

function BillingSummarySection({
  loading,
  summary,
}: {
  loading: boolean;
  summary?: {
    yesterday: {
      storageChargeCredits: number;
      egressChargeCredits: number;
      chargeStatus: string;
    } | null;
    monthToDate: {
      storageChargeCredits: number;
      egressChargeCredits: number;
      daysCharged: number;
      daysFailed: number;
    };
  };
}) {
  if (loading || !summary) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Billing</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }
  const mtdTotal =
    summary.monthToDate.storageChargeCredits + summary.monthToDate.egressChargeCredits;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-sm font-medium">Yesterday</div>
          {summary.yesterday ? (
            <div className="text-sm text-muted-foreground">
              Storage: {summary.yesterday.storageChargeCredits.toFixed(4)} credits · Egress:{' '}
              {summary.yesterday.egressChargeCredits.toFixed(4)} credits ·{' '}
              <span className="font-mono">{summary.yesterday.chargeStatus}</span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No snapshot yet.</div>
          )}
        </div>
        <div>
          <div className="text-sm font-medium">Month to date</div>
          <div className="text-sm text-muted-foreground">
            {mtdTotal.toFixed(4)} credits total · {summary.monthToDate.daysCharged} days charged ·{' '}
            {summary.monthToDate.daysFailed} failed
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Read-only view of the shared assets a publish of this arche would pin —
 * the ACTIVE assets of every workspace library the arche is validly
 * linked to. Curation happens on the Workspace Assets page; this grid only
 * shows what the arche will serve. Renders nothing when the server returns
 * nothing (links are managed in the Workspace links section below).
 *
 * `source=shared` returns ONLY workspace-owned rows, so the section's
 * visibility and the "showing N" copy come straight from the server
 * result; `isWorkspaceOwnedAsset` below is a TYPE narrowing for the
 * workspace-name lookup, not the mechanism that keeps own assets out.
 */
const SHARED_ASSETS_PAGE_LIMIT = 100;

function isWorkspaceOwnedAsset(
  asset: ContributorArcheAsset,
): asset is ContributorArcheAsset & { holderId: string } {
  return asset.holderId !== null;
}

function SharedWorkspaceAssetsGrid({ archeId }: { archeId: string }) {
  const sharedQuery = useArcheSharedAssets(archeId, { limit: SHARED_ASSETS_PAGE_LIMIT });
  const linksQuery = useArcheWorkspaceLinks(archeId);

  const loadedAssets = (sharedQuery.data?.pages ?? []).flatMap((page) => page.assets);
  if (loadedAssets.length === 0) return null;

  const workspaceNameById = new Map(
    (linksQuery.data?.links ?? []).map((l) => [l.holderId, l.displayName] as const),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shared from workspaces</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-muted-foreground">
          These assets come from workspace libraries this arche is linked to and are read-only here.
          The next publish of this arche pins each workspace&apos;s latest published library
          version. Manage them on the Workspace Assets page.
        </p>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {loadedAssets.map((asset) => (
            <AssetCard
              key={asset.assetId}
              asset={asset}
              readOnly
              sourceLabel={
                (isWorkspaceOwnedAsset(asset) && workspaceNameById.get(asset.holderId)) ||
                'a workspace'
              }
              previewUrl={
                asset.status === 'active'
                  ? // The ARCHE preview endpoint also serves validly-linked
                    // shared assets, so the arche page never needs workspace
                    // routes (which a collaborator may not be able to read).
                    api.buildArcheAssetPreviewUrl(archeId, asset.assetId)
                  : null
              }
            />
          ))}
        </div>
        {sharedQuery.hasNextPage && (
          <div className="mt-4 flex flex-col items-center gap-2 text-sm text-muted-foreground">
            <p>
              Showing the first {loadedAssets.length.toLocaleString()} shared asset
              {loadedAssets.length === 1 ? '' : 's'} — manage the full library on the{' '}
              <Link to="/contributor/workspaces" className="text-primary hover:underline">
                Workspace Assets page
              </Link>
              .
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void sharedQuery.fetchNextPage()}
              disabled={sharedQuery.isFetchingNextPage}
            >
              {sharedQuery.isFetchingNextPage ? 'Loading…' : 'Load more shared assets'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
